import { Component, Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { FlaskConical, AudioLines, Maximize2, Minimize2, ChevronDown, Copy, Check } from 'lucide-react';
import Markdown from './Markdown';
import type { Slide, WidgetRef } from '../services/lectureApi';
import { useFeature } from '../../../stores/featureStore';
import { useAuthStore } from '../../../stores/authStore';
import { useLectureStore } from '../store/useLectureStore';
import {
  computeCue,
  prefersReducedMotion,
  slideLayerClass,
  SLIDE_TRANSITION_MS,
  type WidgetCue,
} from '../lib/transitions';

// The lab task owns the widget renderer at features/lab/WidgetRenderer.tsx
// (DEFAULT export). We lazy-load it so this feature compiles and runs even if
// the lab file is briefly absent during parallel development: a missing module
// resolves to a small "not available yet" fallback instead of breaking the
// build/player, and a throwing widget is caught by the boundary below. The lab
// reads its config from `widget.config`; we forward each `widget_ref` verbatim.
type WidgetRendererProps = {
  widget: WidgetRef;
  onComplete?: (result?: unknown) => void;
  // Narration-synced cue (optional; the lab renderer forwards it to widgets).
  cue?: WidgetCue;
};
const LazyWidgetRenderer = lazy<ComponentType<WidgetRendererProps>>(() =>
  // @ts-ignore — module owned by the lab task (#7); may not exist yet during
  // parallel dev. @ts-ignore (not @ts-expect-error) so this stays valid once
  // the file lands. .catch keeps build + runtime green in the meantime.
  import('../../lab/WidgetRenderer')
    .then((m: { default: ComponentType<WidgetRendererProps> }) => ({
      default: m.default,
    }))
    .catch(() => ({ default: MissingWidget }))
);

function MissingWidget() {
  return (
    <p className="text-xs text-ws-ink-3">This interactive element isn’t available yet.</p>
  );
}

// Drives the browser Fullscreen API for a target element. `active` stays in sync
// even when the user exits via Esc, because we subscribe to `fullscreenchange`.
function useFullscreen(target: React.RefObject<HTMLElement | null>) {
  const [active, setActive] = useState(false);

  useEffect(() => {
    const onChange = () =>
      setActive(document.fullscreenElement === target.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [target]);

  const toggle = useCallback(() => {
    const el = target.current;
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen?.().catch(() => {});
    } else {
      el.requestFullscreen?.().catch(() => {});
    }
  }, [target]);

  return { active, toggle };
}

// Top-right control that toggles the slide stage in/out of fullscreen. The icon
// reflects the live fullscreen state (Maximize2 → enter, Minimize2 → exit).
function FullscreenButton({
  active,
  onToggle,
}: {
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      title={active ? 'Exit full screen' : 'Full screen slide'}
      className="absolute right-2 top-2 z-20 grid h-8 w-8 place-items-center rounded-md border border-ws-border bg-ws-surface/90 text-ws-ink-2 shadow-sm backdrop-blur transition-colors hover:bg-ws-surface-2"
    >
      {active ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
    </button>
  );
}

interface SlideStageProps {
  slide: Slide | undefined;
  // Bubble widget completion up so the player can gate "advance-when-covered".
  onWidgetComplete: (widget: WidgetRef) => void;
  // Optional element to put into fullscreen instead of just the stage. The player
  // passes a wrapper that ALSO contains the AudioController, so the play/seek
  // transport stays visible in fullscreen. Falls back to the stage element.
  surfaceRef?: React.RefObject<HTMLElement | null>;
}

// Renders one slide. VISUAL-FIRST: when the slide carries an interactive widget,
// the visual is the prominent centerpiece (large left pane on desktop) and the
// heading + markdown bullets sit alongside in a narrower aside; the narration
// (AudioController, below) is the spoken track the learner listens to. When the
// slide has no widget, the text renders full-width and centered as a reading
// slide. The whole stage is height-bounded to the workspace so an animated
// widget never forces the page to scroll — each pane scrolls internally instead.
export default function SlideStage({ slide, onWidgetComplete, surfaceRef }: SlideStageProps) {
  // Feature gating: quiz widgets respect `quizzes`; all other interactive widgets
  // respect `virtual_lab`. A disabled widget is simply dropped from the slide.
  const quizzesEnabled = useFeature('quizzes');
  const labEnabled = useFeature('virtual_lab');

  // Fullscreen: the stage container fills the screen; widget + narration text
  // stay mounted inside it so both remain visible and usable.
  const stageRef = useRef<HTMLDivElement>(null);
  // Fullscreen the player surface (stage + transport) when the player provides it,
  // so the play/seek controls remain visible; otherwise just the stage.
  const { active: isFullscreen, toggle: toggleFullscreen } = useFullscreen(surfaceRef ?? stageRef);
  const widgets = useMemo<WidgetRef[]>(
    () =>
      (slide?.widget_refs ?? []).filter((w): w is WidgetRef => {
        if (!w) return false;
        const isQuiz = w.type === 'quiz';
        return isQuiz ? quizzesEnabled : labEnabled;
      }),
    [slide, quizzesEnabled, labEnabled]
  );

  // ── Narration-synced cueing ────────────────────────────────────────────────
  // Live transport state from the store drives the cue passed to the slide's
  // primary widget: as the instructor talks, the widget advances/highlights in
  // sync. Duration prefers the manifest value (known before the element loads).
  const currentSlideIndex = useLectureStore((s) => s.currentSlideIndex);
  const audioPosition = useLectureStore((s) => s.audioPosition);
  const isPlaying = useLectureStore((s) => s.isPlaying);
  const audioForSlide = useLectureStore((s) => s.audioForSlide);
  const slideDurationSec = useMemo(() => {
    const ms = audioForSlide(currentSlideIndex)?.duration_ms;
    return ms ? ms / 1000 : 0;
  }, [audioForSlide, currentSlideIndex]);

  // Admin/super-admin gate (same pattern as SlideProgressRail/LeftNav) — drives
  // the collapsible transcript at the bottom of the right panel.
  const user = useAuthStore((s) => s.user);
  const isAdmin = !!(user?.is_admin || user?.is_super_admin);
  // Presenter Notes switch (top-bar toggle). The transcript renders only when an
  // admin/instructor has Notes ON — students watching a presentation never see
  // it, even on an admin account, until the instructor explicitly turns it on.
  const notesVisible = useLectureStore((s) => s.notesVisible);
  const showTranscript = isAdmin && notesVisible;

  // SECONDARY visualization: widget_refs[1] (set by the importer as the slide's
  // `aux_widget`). It renders in the RIGHT panel above body_md, with its OWN
  // narration-synced cue, exactly like the stage widget — independent of the
  // feature-filtered `widgets` list so its index stays pinned to widget_refs[1].
  const auxWidget = useMemo<WidgetRef | null>(() => {
    const w = slide?.widget_refs?.[1];
    if (!w) return null;
    const enabled = w.type === 'quiz' ? quizzesEnabled : labEnabled;
    return enabled ? w : null;
  }, [slide, quizzesEnabled, labEnabled]);
  const auxCue = computeCue(auxWidget ?? undefined, audioPosition, slideDurationSec, isPlaying);

  if (!slide) {
    return (
      <div className="grid h-full place-items-center p-10 text-center text-ws-ink-3">
        <p className="text-sm">This slide has no content.</p>
      </div>
    );
  }

  const hasBody = !!slide.markdown_body && slide.markdown_body.trim().length > 0;
  // The first widget is the slide's centerpiece visual. Any extras stack under
  // the aside text (rare, but handled) — but the aux widget (widget_refs[1]) is
  // rendered explicitly above body_md, so exclude it from the generic stack.
  const [primaryWidget, ...rest] = widgets;
  const extraWidgets = auxWidget ? rest.filter((w) => w !== auxWidget) : rest;

  // Cue only the centerpiece widget — it's the one the narration is describing.
  const primaryCue = computeCue(
    primaryWidget,
    audioPosition,
    slideDurationSec,
    isPlaying
  );

  // Narration progress (0..1) drives the thin accent bar across the visual's
  // header so the learner can feel the slide advancing even without the widget.
  const narrationPct = primaryCue?.progress != null ? primaryCue.progress * 100 : 0;
  const narrating = isPlaying && slideDurationSec > 0;

  // ── Reading slide (no widget): text full-width, centered, scrolls. ──────────
  if (!primaryWidget) {
    return (
      <div ref={stageRef} data-testid="slide-stage" className="relative h-full bg-ws-bg">
        <FullscreenButton active={isFullscreen} onToggle={toggleFullscreen} />
        <SlideFade slideId={slide.id} className="h-full">
        <div className="relative h-full overflow-y-auto">
          <StageBackdrop />
          <article className="relative mx-auto max-w-3xl px-5 py-8 sm:px-8 sm:py-12">
            {slide.heading && (
              <h1 className="mb-5 text-2xl font-semibold leading-tight tracking-tight text-ws-ink sm:text-3xl">
                <span className="mb-3 block h-1 w-12 rounded-full bg-ws-accent" />
                {slide.heading}
              </h1>
            )}
            {hasBody ? (
              <div className="text-[15px] leading-relaxed">
                <Markdown glossary>{slide.markdown_body as string}</Markdown>
              </div>
            ) : (
              !slide.heading && (
                <p className="text-sm text-ws-ink-3">
                  This slide has no written content — listen to the narration.
                </p>
              )
            )}
            {/* Presenter Notes transcript on reading slides too (no right panel here). */}
            {showTranscript && (
              <div className="mx-auto mt-8 max-w-3xl">
                <AdminTranscript text={slide.speaker_script ?? ''} />
              </div>
            )}
          </article>
        </div>
        </SlideFade>
      </div>
    );
  }

  // ── Visual-first slide: interactive centerpiece + concise text aside. ───────
  return (
    <div ref={stageRef} data-testid="slide-stage" className="relative min-h-full bg-ws-bg lg:h-full">
      <FullscreenButton active={isFullscreen} onToggle={toggleFullscreen} />
      <SlideFade
        slideId={slide.id}
        className="relative flex min-h-full flex-col gap-4 p-4 sm:p-5 lg:h-full lg:min-h-0 lg:flex-row lg:gap-5 lg:overflow-hidden"
      >
      <StageBackdrop />

      {/* MAIN — the interactive visual centerpiece (height-bounded). */}
      <section className="relative flex min-h-0 flex-1 lg:basis-[64%]">
        <div className="group relative flex min-h-0 w-full flex-col overflow-hidden rounded-xl border border-ws-tile-border bg-ws-tile shadow-sm ring-1 ring-black/[0.02]">
          {/* Visual header: live narration progress + a soft "now narrating"
              pulse so the centerpiece feels alive while audio plays. */}
          <div className="relative h-1 w-full shrink-0 overflow-hidden bg-ws-surface-2">
            <div
              className="h-full rounded-r-full bg-ws-accent transition-[width] duration-200 ease-linear"
              style={{ width: `${narrationPct}%` }}
            />
          </div>
          {narrating && (
            <div className="pointer-events-none absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-ws-accent-quiet px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-ws-accent-ink shadow-sm">
              <AudioLines size={11} className="animate-pulse" />
              Narrating
            </div>
          )}

          <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-3 sm:p-5">
            <div className="flex max-h-full w-full items-center justify-center">
              <SlideWidget
                widget={primaryWidget}
                cue={primaryCue}
                onComplete={() => onWidgetComplete(primaryWidget)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ASIDE — secondary visual + heading + concise bullets; scrolls if long. */}
      <aside className="relative flex min-h-0 flex-col lg:basis-[36%]">
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {/* SECONDARY visualization (widget_refs[1]) — narration-cued, above the
              body_md summary, same renderer/cue plumbing as the stage widget. */}
          {auxWidget && (
            <div className="mb-4 overflow-hidden rounded-lg border border-ws-tile-border bg-ws-tile shadow-sm ring-1 ring-black/[0.02]">
              <div className="flex items-center justify-center p-3 sm:p-4">
                <SlideWidget
                  widget={auxWidget}
                  cue={auxCue}
                  onComplete={() => onWidgetComplete(auxWidget)}
                />
              </div>
            </div>
          )}
          {slide.heading && (
            <h1 className="mb-3 text-xl font-semibold leading-tight tracking-tight text-ws-ink sm:text-2xl">
              <span className="mb-2.5 block h-1 w-10 rounded-full bg-ws-accent" />
              {slide.heading}
            </h1>
          )}
          {hasBody ? (
            <div className="text-[13.5px] leading-relaxed text-ws-ink-2">
              <Markdown compact glossary>{slide.markdown_body as string}</Markdown>
            </div>
          ) : (
            <p className="text-sm text-ws-ink-3">
              Watch the visual while you listen to the narration.
            </p>
          )}

          {/* Any additional widgets stack beneath the text. */}
          {extraWidgets.length > 0 && (
            <div className="mt-5 space-y-4">
              {extraWidgets.map((w, i) => (
                <div
                  key={w.id ?? i}
                  className="rounded-lg border border-ws-tile-border bg-ws-tile p-3 shadow-sm"
                >
                  <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-ws-ink-3">
                    <FlaskConical size={13} className="text-ws-accent" />
                    {w.type ? w.type.replace(/[_-]/g, ' ') : 'Interactive'}
                  </div>
                  <SlideWidget widget={w} onComplete={() => onWidgetComplete(w)} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Presenter Notes transcript — pinned to the bottom of the right panel.
            Hidden entirely unless the instructor has toggled Notes ON. */}
        {showTranscript && (
          <AdminTranscript text={slide.speaker_script ?? ''} />
        )}
      </aside>
      </SlideFade>
    </div>
  );
}

// Collapsible, admin-only narration transcript. Default collapsed so it never
// clutters the presentation view; a header row toggles it and a copy button
// puts the full narration on the clipboard. Styled with ws- tokens (light/dark).
function AdminTranscript({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    []
  );

  const onCopy = useCallback(async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (copyTimer.current) clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked (e.g. insecure context) — silently no-op */
    }
  }, [text]);

  const hasText = !!text && text.trim().length > 0;

  return (
    <div className="mt-3 shrink-0 rounded-lg border border-ws-border bg-ws-surface">
      <div className="flex items-center">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-1.5 rounded-l-lg px-2.5 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wider text-ws-ink-3 transition-colors hover:bg-ws-surface-2"
          title={open ? 'Hide transcript' : 'Show transcript'}
        >
          <ChevronDown
            size={13}
            className={`shrink-0 transition-transform duration-200 ${open ? '' : '-rotate-90'}`}
          />
          Transcript
        </button>
        <button
          type="button"
          onClick={onCopy}
          disabled={!hasText}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium text-ws-ink-3 transition-colors hover:text-ws-ink-2 disabled:opacity-40"
          title="Copy narration to clipboard"
        >
          {copied ? (
            <>
              <Check size={13} className="text-ws-success" />
              <span className="text-ws-success">Copied</span>
            </>
          ) : (
            <>
              <Copy size={13} />
              <span className="hidden sm:inline">Copy</span>
            </>
          )}
        </button>
      </div>
      {open && (
        <div className="max-h-40 overflow-y-auto border-t border-ws-border px-2.5 py-2 text-[12.5px] leading-relaxed text-ws-ink-2">
          {hasText ? text : (
            <span className="italic text-ws-ink-3">No narration for this slide.</span>
          )}
        </div>
      )}
    </div>
  );
}

// Soft radial wash behind the stage — adds depth so the slide reads as a
// deliberate "stage" rather than a flat card. Token-driven; subtle in both
// light and dark. Purely decorative, never intercepts pointer events.
function StageBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 -z-0 opacity-60"
      style={{
        background:
          'radial-gradient(120% 80% at 50% -10%, var(--ws-accent-quiet) 0%, transparent 55%)',
      }}
    />
  );
}

// Cross-fade + subtle rise/scale between slides — a cinematic dissolve. Keyed by
// slide id: when the slide changes we briefly render the OUTGOING content fading
// out, then mount the new slide and play its enter animation. Respects
// prefers-reduced-motion (collapses to an instant swap).
function SlideFade({
  slideId,
  className,
  children,
}: {
  slideId: string;
  className?: string;
  children: ReactNode;
}) {
  const reduced = useMemo(() => prefersReducedMotion(), []);
  const [entering, setEntering] = useState(true);
  const firstMount = useRef(true);

  // Restart the enter animation on every slide change. We toggle `entering` on
  // for one frame (start state), then off (end state) so the CSS transition
  // runs. requestAnimationFrame guarantees the browser paints the start state
  // before we flip to the end state — otherwise it would skip the animation.
  useEffect(() => {
    if (reduced) {
      setEntering(false);
      return;
    }
    setEntering(true);
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setEntering(false))
    );
    firstMount.current = false;
    return () => cancelAnimationFrame(id);
  }, [slideId, reduced]);

  return (
    <div
      key={slideId}
      className={`will-change-[opacity,transform] transition-[opacity,transform] ease-[var(--ws-ease)] ${slideLayerClass(
        entering,
        reduced
      )} ${className ?? ''}`}
      style={reduced ? undefined : { transitionDuration: `${SLIDE_TRANSITION_MS}ms` }}
    >
      {children}
    </div>
  );
}

// Defensive wrapper around the lab's WidgetRenderer. If the renderer is still a
// stub (or throws on an unknown widget type), the boundary degrades to a small
// note rather than crashing the whole player.
function SlideWidget({
  widget,
  cue,
  onComplete,
}: {
  widget: WidgetRef;
  cue?: WidgetCue;
  onComplete: () => void;
}) {
  return (
    <WidgetBoundary>
      <Suspense
        fallback={
          <p role="status" className="text-xs text-ws-ink-3">
            Loading interactive element…
          </p>
        }
      >
        <LazyWidgetRenderer widget={widget} cue={cue} onComplete={onComplete} />
      </Suspense>
    </WidgetBoundary>
  );
}

class WidgetBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (this.state.failed) {
      return (
        <p className="text-xs text-ws-ink-3">
          This interactive element isn’t available yet.
        </p>
      );
    }
    return this.props.children;
  }
}
